import { sha256 } from 'js-sha256';
import type { Cart, Order, ProductVariant } from '~~/types/commercetools';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import { productGetters, cartGetters } from '~/utils/ct';
import {
  buildAddToCartTags,
  buildBeginCheckoutTags,
  buildClickAlternativeProductTags,
  buildClickProductBoxTags,
  buildPayNowTags,
  buildPromotionTags,
  buildPurchaseTags,
  buildRemarketingTags,
  buildRemoveFromCartTags,
  buildReviewProductTags,
  buildViewAccessoryTags,
  buildViewCartRecommendationsTags,
  buildViewItemListTags,
  buildViewParentProductsTags,
  buildViewProductTags,
  buildViewSimilarProductsTags,
} from '~/plugins/integrations/tagManager/helpers';
import {
  prepareCartRecommendationsTags,
  prepareClickProductBoxTags,
  prepareParentProductsTags,
  prepareProductReviewTags,
  prepareProductTags,
  preparePromotionTags,
  prepareRemarketingTags,
  prepareSimilarProductsTags,
  prepareViewAccessoryItemsTags,
  prepareViewItemListTags,
} from '~/plugins/integrations/tagManager/helpers/transformers';
import { getShippingPrice, getTotalTaxPrice } from '~~/helpers/cart/getTotalPrices';
import type { MappedDiscount } from '~~/types/discounts';
import { getFirstDiscountCode } from '~~/helpers/cart/cartDiscountGetter';
import { getTotalGrossPrice } from '~~/helpers/cart/getTotalPricesModule';
import type { PurchaseUserInfo } from '~~/types/integrations/tagManager/PurchaseTypes';
import { getProductGTIN } from '~~/helpers/Checkout/getProductGTIN';
import {
  A_WARE_PRODUCT_CLICK,
  B_WARE_PRODUCT_CLICK,
  EVENT_PRODUCT_DETAIL,
  EVENT_PRODUCT_REVIEW,
} from '~~/constants/googleTagManager';
import type {
  RemoveFromCartTagDetailsParams,
  TriggerAddToCartTagsParameters,
  TriggerClickProducBoxParameters,
  TriggerPayNowParameters,
  TriggerProductReviewTagsParameters,
  TriggerPromotionTagsParameters,
  TriggerViewItemListParameters,
} from '~~/types/integrations/tagManager/TagManagerIntegration';
import type { RemarketingPageType } from '~~/types/integrations/tagManager/RemarketingPageType';

export const buildViewItemListEvent = (
  parameters: TriggerViewItemListParameters,
  languageAndCountry: string,
) => {

  return buildViewItemListTags({
    id: parameters.id,
    name: parameters.name,
    itemsArray: prepareViewItemListTags(parameters.items, languageAndCountry),
  });

};

export const buildPromotionEvent = (parameters: TriggerPromotionTagsParameters) => {

  const tagsForPromotion = preparePromotionTags(
    parameters.promotionImageName,
    parameters.promotionImagePosition,
  );

  return buildPromotionTags({
    viewPromotions: parameters.isClick ? undefined : tagsForPromotion,
    clickPromotions: parameters.isClick ? tagsForPromotion : undefined,
  });

};

export const buildRemarketingEvent = (purpose: RemarketingPageType, product: ProductVariant) => {

  const tagsForRemarketing = prepareRemarketingTags(purpose, product);

  return buildRemarketingTags(tagsForRemarketing);

};

export const buildPurchaseEvent = ({
  cartOrOrder,
  products,
  orderNumber,
  languageAndCountry,
  currency,
  deliveryDate,
  paymentType,
  paymentName,
  purchaseUserInfo,
}: {
  cartOrOrder: Cart | Order;
  products: ProductVariant[];
  orderNumber: Order['orderNumber'];
  languageAndCountry: string;
  currency: string;
  deliveryDate?: string;
  paymentType?: string;
  paymentName?: string;
  purchaseUserInfo?: PurchaseUserInfo;
}) => {

  const coupon: MappedDiscount['code'] = getFirstDiscountCode(cartOrOrder);

  const tagProducts = products?.length
    ? products.map(product => {

      const gtin = getProductGTIN(product);

      return prepareProductTags(
        { ...product, gtin },
        {
          currency,
          quantity: cartOrOrder.lineItems.find(item =>
            cartGetters.getItemSku(item) === productGetters.getSku(product))?.quantity,
          languageAndCountry,
        });

    })
    : [];

  const attributes = purchaseUserInfo
    ? (() => {

      const { email, phone } = purchaseUserInfo;
      const hashedEmail = email ? sha256(email) : undefined;
      const hashedPhone = phone ? sha256(phone) : undefined;

      return {
        customer_email: email,
        customer_hashed_email: hashedEmail,
        customer_hashed_phone: hashedPhone,
        estimated_delivery_date: deliveryDate,
        // for lt_transactions, ltv, account_age and account_created_date
        // we're abandoning it for now - we'll get back to it after delivering account management
        lt_transactions: undefined,
        ltv: undefined,
        account_age: undefined,
        account_created_date: undefined,
        crm_status: 0 as const,
      };

    })()
    : undefined;

  return buildPurchaseTags({
    tagProducts,
    orderInformation: {
      transaction_id: orderNumber,
      value: getTotalGrossPrice(cartOrOrder),
      tax: getTotalTaxPrice(cartOrOrder),
      shipping: getShippingPrice(cartOrOrder),
      payment_type: paymentType,
      payment_name: paymentName,
      currency,
      coupon,
    },
    attributes,
  });

};

export const buildPayNowEvent = ({
  params,
  languageAndCountry,
  currency,
}: {
  params: TriggerPayNowParameters;
  languageAndCountry: string;
  currency: string;
}) => {

  const { cart, products, paymentMethod, paymentType, purchaseUserInfo, deliveryDate } = params;
  const coupon: MappedDiscount['code'] = getFirstDiscountCode(cart);

  const tagProducts = products?.length
    ? products.map(product =>
      prepareProductTags(
        product,
        {
          currency,
          quantity: cart?.lineItems?.find(item =>
            cartGetters.getItemSku(item) === productGetters.getSku(product))?.quantity,
          languageAndCountry,
        }))
    : [];

  const attributes = purchaseUserInfo
    ? (() => {

      const { email, phone } = purchaseUserInfo;
      const hashedEmail = email ? sha256(email) : undefined;
      const hashedPhone = phone ? sha256(phone) : undefined;

      return {
        customer_email: email,
        customer_hashed_email: hashedEmail,
        customer_hashed_phone: hashedPhone,
        // for lt_transactions, ltv, account_age and account_created_date
        // we're abandoning it for now - we'll get back to it after delivering account management
        lt_transactions: undefined,
        estimated_delivery_date: deliveryDate,
        ltv: undefined,
        account_age: undefined,
        account_created_date: undefined,
        crm_status: 0 as const,
      };

    })()
    : undefined;

  return buildPayNowTags({
    tagProducts,
    orderInformation: {
      value: getTotalGrossPrice(cart),
      tax: getTotalTaxPrice(cart),
      shipping: getShippingPrice(cart),
      payment_method: paymentMethod,
      payment_type: paymentType,
      currency,
      coupon,
    },
    attributes,
  });

};

export const buildViewProductEvent = ({
  params,
  languageAndCountry,
  currency,
}: {
  params: { product: ProductVariant; inStock: boolean; eventName?: string };
  languageAndCountry: string;
  currency: string;
}) => {

  const { product, inStock, eventName = EVENT_PRODUCT_DETAIL } = params;
  const productTags = prepareProductTags(
    product,
    {
      currency,
      languageAndCountry,
      quantity: 1,
      inStock,
    },
  );

  return buildViewProductTags({
    eventName,
    productTags,
    parentCategory: productTags.item_category2,
  });

};

export const buildAddToCartEvent = ({
  params,
  languageAndCountry,
  currency,
}: {
  params: TriggerAddToCartTagsParameters;
  languageAndCountry: string;
  currency: string;
}) => {

  const { product, quantity, textDisplayed, triggeredIn, shouldOpenCart } = params;
  const productTags = prepareProductTags(product, { languageAndCountry, quantity, currency });

  return buildAddToCartTags({ productTags, textDisplayed, triggeredIn, shouldOpenCart });

};

export const buildRemoveFromCartEvent = ({
  product,
  details,
  languageAndCountry,
  currency,
}: {
  product: ProductVariant;
  details: RemoveFromCartTagDetailsParams;
  languageAndCountry: string;
  currency: string;
}) => {

  const detailsExtended = {
    ...details,
    languageAndCountry,
    currency,
  };
  const productTags = prepareProductTags(product, detailsExtended);

  return buildRemoveFromCartTags(productTags, details.textDisplayed, details.triggeredIn);

};

export const buildBeginCheckoutEvent = ({
  cart,
  products,
  languageAndCountry,
  currency,
}: {
  cart: Cart;
  products: ProductVariant[];
  languageAndCountry: string;
  currency: string;
}) => {

  const tagProducts = products?.length
    ? products.map(product =>
      prepareProductTags(
        product,
        {
          currency,
          quantity: cart.lineItems.find(item =>
            cartGetters.getItemSku(item) === productGetters.getSku(product))?.quantity,
          languageAndCountry,
        }))
    : [];

  return buildBeginCheckoutTags({
    tagProducts,
    cartInformation: {
      value: getTotalGrossPrice(cart),
      tax: getTotalTaxPrice(cart),
      currency,
    },
  });

};

export const buildViewAccessoriesEvent = (products: ProductVariant[], parentProductSku: string) => {

  return buildViewAccessoryTags({
    parentProductId: parentProductSku,
    viewAccessoryItems: prepareViewAccessoryItemsTags(products),
  });

};

export const buildViewParentProductsEvent = (products: ProductVariant[], accessoryProductSku: string) => {

  return buildViewParentProductsTags({
    accessorySku: accessoryProductSku,
    viewParentProductItems: prepareParentProductsTags(products),
  });

};

export const buildClickProductBoxEvent = (parameters: TriggerClickProducBoxParameters) => {

  return buildClickProductBoxTags(
    parameters.boxType,
    prepareClickProductBoxTags(parameters.product),
  );

};

export const buildClickAlternativeProductEvent = (isAware: boolean, productSku: string) => {

  return buildClickAlternativeProductTags(
    isAware ? A_WARE_PRODUCT_CLICK : B_WARE_PRODUCT_CLICK,
    productSku,
  );

};

export const buildViewSimilarProductsEvent = (products: ProductVariant[], sourceProductSku: string) => {

  return buildViewSimilarProductsTags({
    sourceSku: sourceProductSku,
    viewSimilarProductItems: prepareSimilarProductsTags(products),
  });

};

export const buildViewCartRecommendationsEvent = (
  products: ProductVariantExtended[],
  sourceProductSku: string,
) => {

  return buildViewCartRecommendationsTags({
    sourceSku: sourceProductSku,
    viewCartRecommendationItems: prepareCartRecommendationsTags(products),
  });

};

export const buildProductReviewEvent = (params: TriggerProductReviewTagsParameters) => {

  const { product, languageAndCountry, score, email, shop, productLink } = params;

  const productTags = prepareProductReviewTags(product, {
    languageAndCountry,
    score,
    email,
    shop,
    productLink,
  });

  return buildReviewProductTags(EVENT_PRODUCT_REVIEW, productTags);

};
